import React, { useState, useEffect, useCallback } from 'react';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Share2, Globe, Building2, Copy, Check, Plus, Trash2,
  DollarSign, Code2, Link as LinkIcon,
  ExternalLink, Eye, X,
  Handshake, BarChart3, CheckCircle2, Clock, XCircle, Loader2,
  TrendingUp, Tag, Grid3x3
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { CATEGORY_OPTIONS } from '../constants/categories';
import apiV2 from '../utils/apiV2';
import { API_URL } from '../config/api';
import axios from 'axios';

const WIDGET_BASE = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
const PLATFORM_MAX_RATE = 25;
const DEFAULT_RATE = 5.0;
const fmt = (n, dec = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// ── Copy Button ──────────────────────────────────────────────────────────────
function CopyBtn({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied!');
  };
  return (
    <button onClick={copy} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-medium transition-colors">
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

// ── Chip ────────────────────────────────────────────────────────────────────
function Chip({ label, icon, selected, onClick, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
        selected
          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
          : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
      }`}
    >
      {icon && <span className="text-base leading-none">{icon}</span>}
      <span>{label}</span>
      {count != null && (
        <span className={`text-xs ${selected ? 'text-blue-100' : 'text-gray-400'}`}>{count}</span>
      )}
    </button>
  );
}

// ── Build embed URLs client-side (env-aware) ──────────────────────────────────
function buildUrls(embed) {
  const ref = embed.ref_key;
  if (embed.mode === 'operator') {
    const snippet = `<!-- BookingFlows Distribution Widget -->\n<iframe src="${WIDGET_BASE}/services-grid.html?org=${embed.target_org_slug}&ref=${ref}"\n  width="100%" height="600" frameborder="0"\n  style="border-radius:12px;overflow:hidden;">\n</iframe>`;
    const directUrl = `${WIDGET_BASE}/host/${embed.target_org_slug}?ref=${ref}`;
    return { snippet, directUrl };
  }
  const cityQ = embed.city ? `city=${encodeURIComponent(embed.city)}&` : '';
  const catQ = embed.category ? `category=${encodeURIComponent(embed.category)}&` : '';
  const snippet = `<!-- BookingFlows Distribution Widget -->\n<iframe src="${WIDGET_BASE}/services-grid.html?${cityQ}${catQ}discovery=true&ref=${ref}"\n  width="100%" height="600" frameborder="0"\n  style="border-radius:12px;overflow:hidden;">\n</iframe>`;
  const directUrl = `${WIDGET_BASE}/marketplace?${cityQ}${catQ}ref=${ref}`;
  return { snippet, directUrl };
}

// ── Embed Card (Flows-style) ──────────────────────────────────────────────────
function EmbedCard({ embed, onDelete }) {
  const [codeMode, setCodeMode] = useState('embed'); // 'embed' | 'url'
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { snippet, directUrl } = buildUrls(embed);
  const previewUrl = `${WIDGET_BASE}/services-grid.html?${embed.city ? `city=${encodeURIComponent(embed.city)}&` : ''}${embed.category ? `category=${encodeURIComponent(embed.category)}&` : ''}discovery=true&ref=${embed.ref_key}&embed=true`;

  const catObj = CATEGORY_OPTIONS.find(c => c.id === embed.category);

  const confirmDelete = async () => {
    setDeleting(true);
    await onDelete();
    setDeleting(false);
    setShowDeleteModal(false);
  };

  return (
    <>
      <div className="bg-white rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)]" data-testid={`embed-card-${embed.id}`}>
        <div className="p-6 pb-4">
          {/* Header row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                {embed.mode === 'city' ? <Globe className="w-5 h-5 text-blue-600" /> : <Building2 className="w-5 h-5 text-blue-600" />}
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{embed.name}</h3>
                <p className="text-sm text-gray-500">
                  {embed.mode === 'city'
                    ? `${embed.city || 'All cities'}${catObj ? ` · ${catObj.icon} ${catObj.name}` : ''}`
                    : embed.target_org_slug
                  } · <span className="font-medium">{embed.rate_pct}% rate</span>
                </p>
              </div>
            </div>
          </div>

          {/* Code type toggle */}
          <div className="flex gap-2 mb-3">
            {[
              { id: 'embed', icon: Code2, label: 'Embed Code' },
              { id: 'url', icon: LinkIcon, label: 'Direct URL' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setCodeMode(opt.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  codeMode === opt.id
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <opt.icon className="w-3.5 h-3.5" />
                {opt.label}
              </button>
            ))}
          </div>

          {/* Code block — Flows style */}
          <div className="relative">
            <div className="bg-gray-900 rounded-xl p-4 pr-12 overflow-x-auto">
              <code className="text-sm font-mono whitespace-nowrap text-blue-300">
                {codeMode === 'embed' ? snippet : directUrl}
              </code>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(codeMode === 'embed' ? snippet : directUrl);
                toast.success('Copied!');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              title="Copy"
            >
              <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            {codeMode === 'embed'
              ? 'Paste this into your website. Only shows services from opted-in operators.'
              : 'Share this link anywhere — social, email, newsletter.'}
          </p>

          {/* Action links */}
          <div className="flex items-center gap-4 text-sm mt-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <a
              href={directUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              Open
            </a>
            <div className="flex-1" />
            <button
              onClick={() => setShowDeleteModal(true)}
              className="text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
              data-testid={`delete-embed-${embed.id}`}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Inline preview */}
        {showPreview && (
          <div className="bg-gray-50 p-4">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <iframe
                src={previewUrl}
                className="w-full border-none"
                style={{ height: '500px' }}
                title={`Preview ${embed.name}`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Embed</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{embed.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Embed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DistributionTab({ organizationId }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [settings, setSettings] = useState(null);
  const [operators, setOperators] = useState([]);
  const [embeds, setEmbeds] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Cities from marketplace API
  const [availableCities, setAvailableCities] = useState([]);

  // Create embed dialog
  const [showCreateEmbed, setShowCreateEmbed] = useState(false);
  const [embedForm, setEmbedForm] = useState({ name: '', mode: 'city', city: null, category: null, target_org_id: '', target_org_slug: '', rate_pct: DEFAULT_RATE });
  const [createdEmbed, setCreatedEmbed] = useState(null);

  // Rate settings
  const [localRate, setLocalRate] = useState(String(DEFAULT_RATE));

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, rRes, aRes] = await Promise.allSettled([
        apiV2.get(`/organizations/${organizationId}/distribution/settings`),
        apiV2.get(`/organizations/${organizationId}/distribution/embeds`),
        apiV2.get(`/organizations/${organizationId}/distribution/referrals`),
        apiV2.get(`/organizations/${organizationId}/distribution/agreements`),
      ]);
      if (sRes.status === 'fulfilled') {
        const s = sRes.value.data;
        setSettings(s);
        setLocalRate(s.distribution_rate != null ? String(s.distribution_rate) : String(DEFAULT_RATE));
      }
      if (eRes.status === 'fulfilled') setEmbeds(eRes.value.data.embeds || []);
      if (rRes.status === 'fulfilled') setReferrals(rRes.value.data);
      if (aRes.status === 'fulfilled') setAgreements(aRes.value.data.agreements || []);
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }, [organizationId]);

  const loadOperators = useCallback(async () => {
    try {
      const res = await apiV2.get(`/organizations/${organizationId}/distribution/operators`);
      setOperators(res.data.operators || []);
    } catch (e) { /* non-fatal */ }
  }, [organizationId]);

  const loadCities = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/marketplace/smart/cities?limit=24`);
      setAvailableCities(res.data.cities || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); loadCities(); }, [load, loadCities]);
  useEffect(() => { if (activeSection === 'as-distributor' || showCreateEmbed) loadOperators(); }, [activeSection, showCreateEmbed, loadOperators]);

  // ── Save rate ──────────────────────────────────────────────────────────────
  const saveRate = async () => {
    const rate = parseFloat(localRate);
    if (!rate || rate < 1 || rate > PLATFORM_MAX_RATE) return toast.error(`Rate must be 1–${PLATFORM_MAX_RATE}%`);
    setSaving(true);
    try {
      await apiV2.put(`/organizations/${organizationId}/distribution/settings`, {
        opted_in: true,
        distribution_rate: rate,
      });
      toast.success(`Distribution rate set to ${rate}%`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  // ── Create embed ───────────────────────────────────────────────────────────
  const openCreateDialog = () => {
    setCreatedEmbed(null);
    setEmbedForm({ name: '', mode: 'city', city: null, category: null, target_org_id: '', target_org_slug: '', rate_pct: parseFloat(localRate) || DEFAULT_RATE });
    setShowCreateEmbed(true);
  };

  const createEmbed = async () => {
    if (!embedForm.name.trim()) return toast.error('Enter a name for this embed');
    if (embedForm.mode === 'operator' && !embedForm.target_org_id) return toast.error('Select an operator');
    setSaving(true);
    try {
      const res = await apiV2.post(`/organizations/${organizationId}/distribution/embeds`, { ...embedForm, city: embedForm.city || null, category: embedForm.category || null });
      setCreatedEmbed({ result: res.data, embed: { ...embedForm, ref_key: res.data.embed?.ref_key || res.data.ref_key } });
      setEmbeds(prev => [...prev, res.data.embed]);
      toast.success('Embed created!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create embed'); }
    setSaving(false);
  };

  const deleteEmbed = async (embedId) => {
    await apiV2.delete(`/organizations/${organizationId}/distribution/embeds/${embedId}`);
    setEmbeds(prev => prev.filter(e => e.id !== embedId));
    toast.success('Embed removed');
  };

  const respondToAgreement = async (id, action) => {
    try {
      await apiV2.put(`/organizations/${organizationId}/distribution/agreements/${id}`, { action });
      toast.success(action === 'accept' ? 'Agreement accepted' : 'Agreement rejected');
      await load();
    } catch (e) { toast.error('Failed to update'); }
  };

  if (loading) return <PageLoader />;

  const pendingAgreements = agreements.filter(a => a.status === 'pending' && a.role === 'operator');
  const catObj = CATEGORY_OPTIONS.find(c => c.id === embedForm.category);

  return (
    <TabPageLayout
      title="Distribution Network"
      subtitle="Earn by distributing others' experiences — or let partners list yours"
      actions={
        activeSection === 'as-distributor' && (
          <Button onClick={openCreateDialog} className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-embed-btn">
            <Plus className="w-4 h-4" /> New Embed
          </Button>
        )
      }
    >
      {/* Section Nav */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit flex-wrap mb-6" data-testid="distribution-section-nav">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'as-operator', label: `My Rate${pendingAgreements.length ? ` (${pendingAgreements.length})` : ''}` },
          { id: 'as-distributor', label: 'Embeds' },
          { id: 'referrals', label: 'Referrals' },
          { id: 'agreements', label: 'Agreements' },
        ].map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            data-testid={`distribution-nav-${s.id}`}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all min-h-[44px] ${
              activeSection === s.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ──────────────────────────────────────────────────────────── */}
      {activeSection === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: TrendingUp, label: 'Referred Bookings', value: referrals?.total_bookings || 0, color: 'text-blue-600 bg-blue-50' },
              { icon: DollarSign, label: 'Revenue Generated', value: `$${fmt(referrals?.total_revenue)}`, color: 'text-green-600 bg-green-50' },
              { icon: DollarSign, label: 'Your Earnings', value: `$${fmt(referrals?.total_earnings)}`, color: 'text-purple-600 bg-purple-50' },
              { icon: Code2, label: 'Active Embeds', value: embeds.length, color: 'text-amber-600 bg-amber-50' },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color}`}><Icon size={18} /></div>
                <div className="text-2xl font-bold text-gray-900">{value}</div>
                <div className="text-sm text-gray-500 mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-2">
              <div className="flex items-center gap-2 font-semibold text-gray-800">
                <Building2 size={15} className="text-blue-600" /> As an Operator
              </div>
              <p className="text-sm text-gray-500">Set one rate. Partners who send you bookings earn that % from your revenue. Customer price never changes.</p>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${settings?.marketplace_enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {settings?.marketplace_enabled ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                {settings?.marketplace_enabled
                  ? `Listed at ${settings.distribution_rate ?? DEFAULT_RATE}%`
                  : 'Enable marketplace to participate'}
              </div>
              <button onClick={() => setActiveSection('as-operator')} className="text-xs text-blue-600 font-medium hover:underline block pt-1">Set my rate →</button>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-2">
              <div className="flex items-center gap-2 font-semibold text-gray-800">
                <Globe size={15} className="text-purple-600" /> As a Distributor
              </div>
              <p className="text-sm text-gray-500">Embed any city or operator's experiences on your site. Earn a cut on every booking. One snippet, always live.</p>
              <div className="text-xs text-gray-500 font-medium">{embeds.length} embed{embeds.length !== 1 ? 's' : ''} active</div>
              <button onClick={() => setActiveSection('as-distributor')} className="text-xs text-purple-600 font-medium hover:underline block pt-1">Create embed →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MY RATE ───────────────────────────────────────────────────────────── */}
      {activeSection === 'as-operator' && (
        <div className="space-y-5 max-w-xl">
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
            <h2 className="font-semibold text-gray-900">My Distribution Rate</h2>
            {!settings?.marketplace_enabled ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                Enable your marketplace listing to participate in the distribution network. Go to the <strong>Marketplace</strong> tab to enable it.
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  Since your marketplace is enabled, your services are automatically available to distribution partners. Set the % of revenue you'll share when they refer a booking.
                </p>
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-xs text-blue-700 flex items-center gap-2">
                  <CheckCircle2 size={13} /> You're in the distribution network. Any partner can embed and promote your services.
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Referral rate (%)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range" min="1" max={PLATFORM_MAX_RATE} step="0.5"
                      value={parseFloat(localRate) || DEFAULT_RATE}
                      onChange={e => setLocalRate(e.target.value)}
                      className="flex-1 h-2 rounded-lg appearance-none bg-gray-200 accent-blue-600"
                      data-testid="distribution-rate-slider"
                    />
                    <div className="w-20">
                      <Input type="number" min="1" max={PLATFORM_MAX_RATE} step="0.5" value={localRate}
                        onChange={e => setLocalRate(e.target.value)} className="text-center font-bold" data-testid="distribution-rate-input" />
                    </div>
                    <span className="text-gray-500 text-sm font-medium">%</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    On a $100 booking, you'd pay ${fmt(parseFloat(localRate) || DEFAULT_RATE)} to the partner.
                  </p>
                </div>
                <Button onClick={saveRate} disabled={saving} data-testid="distribution-save-settings">
                  {saving ? 'Saving...' : 'Save Rate'}
                </Button>
              </>
            )}
          </div>

          {pendingAgreements.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-amber-900 flex items-center gap-2">
                <Handshake size={16} /> Pending Requests ({pendingAgreements.length})
              </h3>
              {pendingAgreements.map(a => (
                <div key={a.id} className="flex items-center justify-between bg-white rounded-lg border border-amber-100 p-4">
                  <div>
                    <div className="font-medium text-gray-800">{a.distributor?.name}</div>
                    <div className="text-sm text-gray-500">Wants to list your services at <span className="font-semibold text-amber-700">{a.rate_pct}%</span></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => respondToAgreement(a.id, 'reject')} className="text-red-600 border-red-200">Decline</Button>
                    <Button size="sm" onClick={() => respondToAgreement(a.id, 'accept')} className="bg-green-600 hover:bg-green-700">Accept</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── EMBEDS (Flows-style) ───────────────────────────────────────────────── */}
      {activeSection === 'as-distributor' && (
        <div className="space-y-4">
          {embeds.length === 0 ? (
            <EmptyState
              icon={<Globe className="w-10 h-10" />}
              title="No embeds yet"
              description="Create a distribution embed to list experiences on any website and earn a referral cut."
              action={
                <Button onClick={openCreateDialog} className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4" /> Create Your First Embed
                </Button>
              }
            />
          ) : (
            embeds.map(embed => (
              <EmbedCard key={embed.id} embed={embed} onDelete={() => deleteEmbed(embed.id)} />
            ))
          )}
        </div>
      )}

      {/* ── REFERRALS ─────────────────────────────────────────────────────────── */}
      {activeSection === 'referrals' && (
        <div className="space-y-5">
          <h2 className="font-semibold text-gray-900">Referral Report</h2>
          {!referrals || referrals.total_bookings === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-400 space-y-2">
              <BarChart3 size={28} className="mx-auto opacity-40" />
              <p className="text-sm">No referred bookings yet. Create a distribution embed and start sending traffic.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Total Bookings', value: referrals.total_bookings },
                  { label: 'Revenue Sent', value: `$${fmt(referrals.total_revenue)}` },
                  { label: 'Your Earnings', value: `$${fmt(referrals.total_earnings)}` },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                    <div className="text-sm text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>{['Operator', 'Bookings', 'Revenue', 'Rate', 'Your Cut'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {referrals.breakdown.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{row.operator_name}</td>
                        <td className="px-4 py-3 text-gray-600">{row.booking_count}</td>
                        <td className="px-4 py-3 text-gray-600">${fmt(row.revenue)}</td>
                        <td className="px-4 py-3 text-gray-500">{row.rate_pct}%</td>
                        <td className="px-4 py-3 font-semibold text-green-700">${fmt(row.your_earnings)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── AGREEMENTS ────────────────────────────────────────────────────────── */}
      {activeSection === 'agreements' && (
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900">Private Agreements</h2>
          <p className="text-sm text-gray-500">Custom 1-to-1 rates with a specific operator, overriding their default rate.</p>
          {agreements.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-400 text-sm">No private agreements yet.</div>
          ) : (
            agreements.map(a => {
              const StatusIcon = { active: CheckCircle2, pending: Clock, rejected: XCircle }[a.status] || Clock;
              const statusColor = { active: 'text-green-600', pending: 'text-amber-600', rejected: 'text-red-500' }[a.status];
              return (
                <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-800">
                      {a.role === 'distributor' ? `You → ${a.target?.name}` : `${a.distributor?.name} → You`}
                    </div>
                    <div className="text-sm text-gray-500">Rate: <span className="font-semibold">{a.rate_pct}%</span></div>
                  </div>
                  <div className={`flex items-center gap-1.5 text-sm font-medium ${statusColor}`}>
                    <StatusIcon size={15} />
                    {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── CREATE EMBED DIALOG ──────────────────────────────────────────────── */}
      <Dialog open={showCreateEmbed} onOpenChange={setShowCreateEmbed}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{createdEmbed ? 'Embed Created!' : 'Create Distribution Embed'}</DialogTitle>
            {!createdEmbed && <DialogDescription>Generate an embed or link to earn referral commissions.</DialogDescription>}
          </DialogHeader>

          {createdEmbed ? (
            // Result view (Flows-style)
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                <CheckCircle2 size={16} /> Your embed is ready to use
              </div>
              {['snippet', 'directUrl'].map(type => {
                const { snippet, directUrl } = buildUrls(createdEmbed.embed);
                const text = type === 'snippet' ? snippet : directUrl;
                const label = type === 'snippet' ? 'Embed Snippet' : 'Direct URL';
                return (
                  <div key={type}>
                    <div className="text-sm font-medium text-gray-700 mb-1.5">{label}</div>
                    <div className="relative">
                      <div className="bg-gray-900 rounded-xl p-4 pr-12 overflow-x-auto">
                        <code className="text-xs font-mono text-blue-300 whitespace-pre-wrap">{text}</code>
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(text); toast.success('Copied!'); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gray-800 hover:bg-gray-700"
                      >
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                );
              })}
              <Button onClick={() => { setShowCreateEmbed(false); setCreatedEmbed(null); }} className="w-full">Done</Button>
            </div>
          ) : (
            <div className="space-y-5 py-2">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Embed name <span className="text-red-500">*</span></label>
                <Input
                  placeholder="e.g. Austin Watersports Feed"
                  value={embedForm.name}
                  onChange={e => setEmbedForm(f => ({ ...f, name: e.target.value.slice(0, 100) }))}
                  maxLength={100}
                  data-testid="embed-name-input"
                />
                <p className="text-xs text-gray-400 mt-1">{embedForm.name.length}/100</p>
              </div>

              {/* Mode */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">What are you distributing?</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'city', icon: Globe, label: 'City / Category Feed', sub: 'e.g. All Austin experiences' },
                    { id: 'operator', icon: Building2, label: 'Specific Operator', sub: 'e.g. Just BOATX services' },
                  ].map(m => (
                    <button key={m.id} type="button" onClick={() => setEmbedForm(f => ({ ...f, mode: m.id }))}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${embedForm.mode === m.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <m.icon size={15} className="mb-1 text-gray-600" />
                      <div className="text-sm font-medium">{m.label}</div>
                      <div className="text-xs text-gray-400">{m.sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* CITY mode */}
              {embedForm.mode === 'city' && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">City</label>
                      {embedForm.city && <button type="button" onClick={() => setEmbedForm(f => ({ ...f, city: null }))} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5"><X size={10} /> Clear</button>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Chip label="All Cities" icon="🌍" selected={!embedForm.city} onClick={() => setEmbedForm(f => ({ ...f, city: null }))} />
                      {availableCities.map(c => (
                        <Chip key={c.city} label={c.city} selected={embedForm.city === c.city} onClick={() => setEmbedForm(f => ({ ...f, city: c.city }))} count={c.operatorCount > 0 ? `${c.operatorCount}` : undefined} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1"><Tag size={12} /> Category <span className="text-gray-400 font-normal">(optional)</span></label>
                      {embedForm.category && <button type="button" onClick={() => setEmbedForm(f => ({ ...f, category: null }))} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5"><X size={10} /> Clear</button>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORY_OPTIONS.map(cat => (
                        <Chip key={cat.id} label={cat.name} icon={cat.icon} selected={embedForm.category === cat.id} onClick={() => setEmbedForm(f => ({ ...f, category: f.category === cat.id ? null : cat.id }))} />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* OPERATOR mode */}
              {embedForm.mode === 'operator' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Select operator</label>
                  {operators.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 border border-dashed rounded-xl text-sm">
                      <Building2 size={22} className="mx-auto mb-2 opacity-40" />
                      No marketplace operators found.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto border rounded-xl p-2 bg-gray-50">
                      {operators.map(op => (
                        <label key={op.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${embedForm.target_org_id === op.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-white border border-transparent'}`}>
                          <input type="radio" name="target_op" checked={embedForm.target_org_id === op.id} onChange={() => setEmbedForm(f => ({ ...f, target_org_id: op.id, target_org_slug: op.slug }))} className="accent-blue-600" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">{op.name}</div>
                            <div className="text-xs text-gray-400">{op.city} · {op.distribution_rate}% rate</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rate */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Your target referral rate (%)</label>
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max={PLATFORM_MAX_RATE} step="0.5" value={embedForm.rate_pct}
                    onChange={e => setEmbedForm(f => ({ ...f, rate_pct: parseFloat(e.target.value) }))}
                    className="flex-1 h-2 rounded-lg appearance-none bg-gray-200 accent-blue-600" />
                  <span className="text-lg font-bold text-blue-700 w-12 text-right">{embedForm.rate_pct}%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">You'll earn the lower of this and the operator's set rate.</p>
              </div>
            </div>
          )}

          {!createdEmbed && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateEmbed(false)}>Cancel</Button>
              <Button onClick={createEmbed} disabled={saving} data-testid="create-embed-submit">
                {saving ? 'Creating...' : 'Generate Embed'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
}
